import React, { useState } from 'react';
import { IconClose } from '../../assets/svg/icons';
import {
  StyledCertBadge,
  StyledCertBadges,
  StyledCloseButton,
  StyledFullDescription,
  StyledModalCard,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalTitle,
  StyledOverlay,
  StyledPrimaryButton,
  StyledProductDetailBody,
  StyledProductImageLarge,
  StyledProductInfo,
  StyledQtyInput,
  StyledQtyLabel,
  StyledQtyRow,
  StyledQtyStepper,
  StyledSecondaryButton,
  StyledSpecsList,
  StyledStepperButton,
  StyledTotalLabel,
  StyledTotalRow,
  StyledTotalValue,
  StyledCardCategory,
} from './styles';
import { Product } from './types';

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onAddToCart: (product: Product, qty: number) => void;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  onClose,
  onAddToCart,
}) => {
  const [qty, setQty] = useState<number>(product.minQty);

  const total = qty * product.priceQI;

  const decrement = () => {
    setQty((q) => Math.max(product.minQty, q - product.stepQty));
  };

  const increment = () => {
    setQty((q) => q + product.stepQty);
  };

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) setQty(Math.max(product.minQty, v));
  };

  const handleAddToCart = () => {
    onAddToCart(product, qty);
    onClose();
  };

  return (
    <StyledOverlay onClick={onClose}>
      <StyledModalCard onClick={(e) => e.stopPropagation()}>
        <StyledModalHeader>
          <StyledModalTitle>Product Details</StyledModalTitle>
          <StyledCloseButton onClick={onClose} type='button' aria-label='Close'>
            <IconClose />
          </StyledCloseButton>
        </StyledModalHeader>

        <StyledProductDetailBody>
          <StyledProductImageLarge bg={product.bg}>
            {product.emoji}
          </StyledProductImageLarge>

          <StyledProductInfo>
            <StyledCardCategory>{product.category}</StyledCardCategory>
            <h2
              style={{
                margin: '0.25rem 0 0',
                fontSize: '1.25rem',
                fontWeight: 700,
              }}
            >
              {product.name}
            </h2>
            <StyledFullDescription>
              {product.fullDescription}
            </StyledFullDescription>

            <StyledSpecsList>
              <dt>Origin</dt>
              <dd>{product.origin}</dd>
              <dt>Packaging</dt>
              <dd>{product.packaging}</dd>
              <dt>Shelf Life</dt>
              <dd>{product.shelfLife}</dd>
              <dt>Unit Price</dt>
              <dd>{product.priceQI.toLocaleString()} QI</dd>
              <dt>Min. Order</dt>
              <dd>{product.minQty} units</dd>
              <dt>Increments</dt>
              <dd>{product.stepQty} units</dd>
            </StyledSpecsList>

            <StyledCertBadges>
              {product.certifications.map((c) => (
                <StyledCertBadge key={c}>{c}</StyledCertBadge>
              ))}
            </StyledCertBadges>

            <StyledQtyRow>
              <StyledQtyLabel>
                <strong>Order Quantity</strong>
                Min. {product.minQty} units · in steps of {product.stepQty}
              </StyledQtyLabel>
              <StyledQtyStepper>
                <StyledStepperButton
                  type='button'
                  onClick={decrement}
                  disabled={qty <= product.minQty}
                  aria-label='Decrease'
                >
                  −
                </StyledStepperButton>
                <StyledQtyInput
                  type='number'
                  value={qty}
                  onChange={handleQtyChange}
                  min={product.minQty}
                  step={product.stepQty}
                />
                <StyledStepperButton
                  type='button'
                  onClick={increment}
                  aria-label='Increase'
                >
                  +
                </StyledStepperButton>
              </StyledQtyStepper>
            </StyledQtyRow>
          </StyledProductInfo>
        </StyledProductDetailBody>

        <StyledTotalRow>
          <StyledTotalLabel>
            Estimated total ({qty.toLocaleString()} units)
          </StyledTotalLabel>
          <StyledTotalValue>{total.toLocaleString()} QI</StyledTotalValue>
        </StyledTotalRow>

        <StyledModalFooter>
          <StyledSecondaryButton type='button' onClick={onClose}>
            Continue Shopping
          </StyledSecondaryButton>
          <StyledPrimaryButton type='button' onClick={handleAddToCart}>
            Add to Cart
          </StyledPrimaryButton>
        </StyledModalFooter>
      </StyledModalCard>
    </StyledOverlay>
  );
};

export default ProductDetailModal;
